import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

// Define the content for the multi-step text module
const moduleContent: ModuleSection[] = [
  {
    title: 'Integration: Making Spirituality Your Way of Life',
    content: [
      'This final module is the most important one because it\'s where all the separate practices–Connectedness, Mindfulness, Compassion, Purpose, Gratitude, and Peace–come together to become who you are.',
      'It\'s the shift from doing spiritual practices to living a spiritual life.'
    ],
    additionalSection: {
      title: 'Meaning-Making: Finding Your Inner Compass',
      content: [
      'Spiritual growth isn\'t about adopting someone else\'s belief system; it\'s about facilitating your own meaning-making. It means trusting that you have an inner compass–a core sense of truth and direction. The whole journey has been about cleaning that compass so you can finally see which way it\'s pointing. The goal is to always look inward, through reflection and empathy, to find the answers that are already yours.',
      ]
    },
  },
  {
    title: 'Your Core Habits for a Grounded Life',
    content: [
      'The skills listed for the coach are actually excellent habits for anyone looking to live an integrated spiritual life. They ensure you stay connected to your truth every day.',
      '• <strong>Active Presence (Be Here):</strong> This is your commitment to show up fully in your own life. It means listening to others and listening to yourself–not just the words, but the subtle tone, silence, and emotion that carry deeper truths.',
      '• <strong>Reflective Questioning (Look Deeper):</strong> Regularly ask yourself the big questions. If you ask, "What gives my life meaning right now?" you force yourself past trivial concerns and back to your core values and purpose. It keeps your life intentional.',
      '• <strong>Empathetic Space-Holding (Acceptance):</strong> This is the ability to handle strong emotions–yours and others\'–without panicking or trying to force a fix. You learn to allow tears, laughter, or silence because you know emotions are passing visitors, and simply giving them space is the kindest, most healing thing you can do.',
      '• <strong>Daily Integration (Consistency Wins):</strong> You don\'t need huge gestures; you need small, consistent habits. A moment of gratitude, a mindful breath, or a simple act of kindness every day builds the quiet strength of your spiritual foundation. Consistency is the key to lasting change.',
      '• <strong>Authentic Living (Walk Your Talk):</strong> This is the most honest part of integration. It means living the peace and compassion you\'ve worked so hard to cultivate. When your external actions match your internal values, you stop wasting energy on conflict and your life becomes a natural inspiration to others.',
    ],
  },
  {
    title: 'Final Integration: Your Spiritual Manifesto',
    content: [
      'The final reflection is your opportunity to define the integrated self you\'ve become.',
      '<strong>1. Define Your Spirituality:</strong> Clearly articulate what spirituality means to you, personally. This clarifies your foundation.',
      '<strong>2. Commit to Your Practice:</strong> Write down your own non-negotiable daily spiritual practice. This ensures the well of peace and wisdom never runs dry.',
      '<strong>3. Commit to Living:</strong> Decide how you will engage with the world–with compassion, respect, and presence. This is your personal promise to show up as your best self.',
      '<br/>',
      '<strong>Key Message:</strong>',
      'The ultimate takeaway is simple, profound, and deeply empowering: Spiritual wellness is a living practice. All the work you\'ve done simply helps you remember your own truth so you can live it fully.'
    ]
  },
  {
    title: 'Course Summary: You Are the Practice',
    content: [
      'Spiritual wellness is a living practice. It flows through your connection to others, your presence in the moment, your kindness, your appreciation, and your inner calm.',
      'You don\'t need to lead anyone toward your truth–you just need to remember your own.',
    ],
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed the module content! Now, let\'s reinforce your learning with a quick quiz to check your understanding.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about integrating spiritual growth in coaching practice.',
        'The quiz will help you review key concepts about spiritual coaching, facilitating meaning-making, and helping clients discover their inner compass.'
      ]
    }
  },
];

// Main component, matching the design of the standard assessment form
const SpiritualModule7Page = () => {
  const navigate = useNavigate();
  const { currentStep: rawCurrentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM7');
  const totalSteps = moduleContent.length;

  // Ensure currentStep is within valid bounds
  const currentStep = Math.min(rawCurrentStep, totalSteps - 1);

  const currentModule = moduleContent[currentStep];
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep);
    } else {
      // Mark the module as completed and navigate to quiz
      await saveProgress(currentStep, true);
      navigate('/panel/learn/spiritual-module-7/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/spiritual-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep);
  };

  const isCompleted = currentStep === totalSteps - 1;

  return (
    // 1. Updated wrapper class: module-form-page -> module-form
    <div className="module-form">
      <div className="module-container">
        
        {/* Left Panel - Illustration */}
        {/* 2. Updated left panel class: module-left-panel -> module-left */}
        <div 
          className="module-left spiritual-panel"
        >
          {/* 3. Retaining module-header for left panel content */}
          <div className="module-header">
            <h1>Module 7</h1>
            <p className="module-subtitle">Integrating Spiritual Growth in Coaching Practice</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
            Spiritual coaching is not about preaching or converting – it's about facilitating meaning-making.
            </p>
            <p className="module-tool-info">
            A good coach helps clients discover their inner compass through reflection, empathy, and consistent practice.
            </p>
          </div>
        </div>

        {/* Right Panel - Content */}
        {/* 4. Updated right panel class: module-right-panel -> module-right */}
        <div className="module-right">
          
          {/* Content Wrapper for flex space-between layout */}
          <div className="module-content"> 
          
            {/* Progress Bar */}
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            {/* Content Section */}
            <div className="module-section">
              <h2 className="module-title"> 
                {` ${currentModule.title}`}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {/* Additional Title Sections */}
            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              // Logic moved to handleBack, so button is never disabled until the end state is reached.
              disabled={false} 
            >
              {currentStep === 0 ? 'Exit Module' : 'Back'}
            </button>
            
            <button
              className="btn-primary"
              onClick={handleNext}
            >
              {isCompleted ? 'Take the Quiz' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule7Page;

